import { Box, Flex } from '@chakra-ui/react'
import { Outlet } from '@tanstack/react-router'
import { MainNavigationTabBar } from './MainNavigationTabBar'
import { DesktopNavRail, DESKTOP_NAV_RAIL_WIDTH } from './DesktopNavRail'
import { MenuOverlay } from '@/components/menu/MenuOverlay'
import { useIsQuizTaking } from '@/components/nav/useNavGateBehaviors'
import { useIsDesktop } from '@/hooks/useIsDesktop'

interface TabLayoutProps {
  children?: React.ReactNode
}

export function TabLayout({ children }: TabLayoutProps) {
  // Drives the mobile bottom bar / desktop left rail cutover (D45b Phase 2).
  // `lg` = 992px, Chakra's default, unmodified by theme.ts. Uses the shared
  // `useIsDesktop()` (also now AppHeader.tsx's own
  // breakpoint source) rather than a bare
  // `useBreakpointValue` call — Chakra's own `ssr: true` default previously
  // resolved to `false` on this SPA's very first client render regardless
  // of the real viewport, dropping the rail (and its logo) from the first
  // paint at `lg`+ until a post-paint `useEffect` corrected it a frame
  // later — a visible flash, not just a wasted render (see
  // `useIsDesktop.ts`'s own docblock).
  const isDesktop = useIsDesktop()
  // DesktopNavRail renders `null` while a quiz is being taken (D45b), so the
  // rail-width gutter below must be driven by the same boolean — otherwise
  // quiz pages get a 224px empty gutter with nothing rendered into it (D45b).
  const isQuizTaking = useIsQuizTaking()
  const reservesRailGutter = isDesktop && !isQuizTaking

  return (
    <Flex direction="column" h="100dvh" bg="transparent">
      {/* Let individual pages (via PageWrapper) handle their own scrolling and padding */}
      <Box flex="1" overflow="hidden" pl={reservesRailGutter ? DESKTOP_NAV_RAIL_WIDTH : 0}>
        {children || <Outlet />}
      </Box>
      {isDesktop ? <DesktopNavRail /> : <MainNavigationTabBar />}
      <MenuOverlay />
    </Flex>
  )
}

export default TabLayout
